import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Ban, Printer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";
import { api, errMsg, uuid, download } from "../lib/api";
import { Btn, Card, Field, Input, Select, TextArea, PageHeader, Money, Empty, Th, Td, Stat, useNames } from "../components/common";
import ScopePicker from "../components/ScopePicker";

const TYPE = { household: "For the household", individual: "Own contribution" };

function DuesForm({ meta, user, onSaved }) {
  const blank = () => ({ gkk_id: user.gkk_id || "", unit_id: user.unit_id || "", payer_name: "", payer_type: "household", amount: "", date: meta.today, month_covered: meta.today.slice(0, 7), note: "", client_uuid: uuid() });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dues", { ...f, unit_id: f.unit_id || null });
      toast.success(`Recorded ${data.ref_no}`, { action: { label: "Print receipt", onClick: () => receipt(data) }, duration: 8000 });
      if (data.possible_duplicate_of) toast.warning(`${data.payer_name} already has a payment for ${data.month_covered} (${data.possible_duplicate_of}). Check it is not a double entry.`, { duration: 9000 });
      setF({ ...blank(), gkk_id: f.gkk_id, unit_id: f.unit_id, date: f.date, month_covered: f.month_covered });
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="space-y-4" data-testid="dues-form">
      <h2 className="font-serif text-lg font-bold">Record a monthly dues payment</h2>
      <ScopePicker gkk={f.gkk_id} unit={f.unit_id} forWrite testid="dues-form-scope" onChange={(g, u) => setF({ ...f, gkk_id: g, unit_id: u })} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name of member / household representative"><Input data-testid="dues-payer-name-input" value={f.payer_name} onChange={(e) => setF({ ...f, payer_name: e.target.value })} placeholder="e.g. Juan Dela Cruz" /></Field>
        <Field label="Paid as"><Select data-testid="dues-payer-type-select" value={f.payer_type} onChange={(e) => setF({ ...f, payer_type: e.target.value })}>{Object.entries(TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
        <Field label="Amount (₱)"><Input data-testid="dues-amount-input" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" /></Field>
        <Field label="Date paid"><Input data-testid="dues-date-input" type="date" max={meta.today} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Month covered"><Input data-testid="dues-month-input" type="month" value={f.month_covered} onChange={(e) => setF({ ...f, month_covered: e.target.value })} /></Field>
        <Field label="Note (optional)"><TextArea data-testid="dues-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
      </div>
      <Btn v="primary" loading={busy} disabled={!f.gkk_id || !f.payer_name.trim() || !f.amount} onClick={save} data-testid="dues-save-button"><Plus className="h-5 w-5" /> Record payment</Btn>
    </Card>
  );
}

function VoidDialog({ row, onClose }) {
  const [reason, setReason] = useState("");
  const go = async () => { try { await api.post(`/dues/${row.id}/void`, { reason }); toast.success("Payment cancelled"); onClose(true); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-white" data-testid="dues-void-dialog">
        <DialogHeader><DialogTitle>Cancel {row.ref_no}</DialogTitle></DialogHeader>
        <p className="text-sm">{row.payer_name} · <Money c={row.amount_cents} /> · {row.month_covered}. The entry stays on record, marked cancelled.</p>
        <Field label="Reason"><TextArea data-testid="dues-void-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <Btn v="danger" disabled={reason.trim().length < 5} onClick={go} data-testid="dues-void-confirm-button"><Ban className="h-5 w-5" /> Cancel payment</Btn>
      </DialogContent>
    </Dialog>
  );
}

const receipt = (r) => download(`/dues/${r.id}/receipt`, {}, `dues-receipt-${r.ref_no}.pdf`).catch(() => toast.error("Download failed"));

function CardDialog({ who, onClose }) {
  const [year, setYear] = useState(Number(who.month_covered.slice(0, 4)));
  const [c, setC] = useState(null);
  const params = { gkk_id: who.gkk_id, unit_id: who.unit_id || "gkk", payer_name: who.payer_name, year };
  useEffect(() => {
    api.get("/dues/card", { params: { gkk_id: who.gkk_id, unit_id: who.unit_id || "gkk", payer_name: who.payer_name, year } }).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [who, year]);
  const pdf = () => download("/dues/card/pdf", params, `dues-card-${year}.pdf`).catch(() => toast.error("Download failed"));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-auto" data-testid="dues-card-dialog">
        <DialogHeader><DialogTitle>Monthly Dues Card — {who.payer_name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Year"><Input data-testid="dues-card-year-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" /></Field>
          <Btn v="primary" onClick={pdf} data-testid="dues-card-print-button"><Printer className="h-5 w-5" /> Print card</Btn>
        </div>
        {c && <>
          <p className="text-sm text-[#6E6259]">{c.gkk_name} · {c.unit_name} · {c.months_paid} of 12 months paid · Total <b data-testid="dues-card-total"><Money c={c.total_cents} /></b></p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="dues-card-months">{c.months.map((m) => (
            <div key={m.month} className={`rounded-xl border p-2 text-sm ${m.paid_cents > 0 ? "border-[#2D6A4F]/50 bg-[#EEF6F1]" : "border-[#E4DACD]"}`} data-testid={`dues-card-month-${m.month}`}>
              <div className="font-bold">{m.label}</div>
              {m.payments.length ? m.payments.map((p) => <div key={p.id} className={`text-xs ${p.status === "void" ? "line-through opacity-60" : ""}`}>{p.date} · <Money c={p.amount_cents} /></div>) : <div className="text-xs text-[#9A8F86]">not paid</div>}
            </div>))}</div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function Unpaid({ fl }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!fl.month) return;
    api.get("/dues/unpaid", { params: { month: fl.month, gkk_id: fl.gkk || undefined, unit_id: fl.unit || undefined } }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [fl.month, fl.gkk, fl.unit]);
  if (!d) return null;
  return (
    <Card className="space-y-3" data-testid="dues-unpaid">
      <h2 className="font-serif text-lg font-bold">Not yet paid for {fl.month} <span className="text-sm font-normal text-[#6E6259]">({d.households} household(s), {d.total - d.households} individual(s))</span></h2>
      <p className="text-xs text-[#6E6259]">Names that paid at least once in the past 12 months but have no payment recorded for this month.</p>
      {!d.units.length ? <Empty>Everyone who paid in the past 12 months has paid for this month.</Empty> : d.units.map((g) => (
        <div key={`${g.gkk_id}-${g.unit_id}`} className="rounded-xl border border-[#E4DACD] p-3" data-testid={`dues-unpaid-unit-${g.unit_id || g.gkk_id}`}>
          <div className="font-bold text-sm mb-1">{g.gkk_name} · {g.unit_name} <span className="font-normal text-[#6E6259]">({g.payers.length})</span></div>
          <ul className="text-sm grid sm:grid-cols-2 gap-x-6">{g.payers.map((p) => (
            <li key={p.payer_name} className="flex justify-between gap-2 border-b border-[#F3EDE2] py-1"><span>{p.payer_name}{p.payer_type === "individual" ? " (own)" : ""}</span><span className="text-xs text-[#6E6259]">last paid {p.last_month_paid} · <Money c={p.last_amount_cents} /></span></li>))}</ul>
        </div>))}
    </Card>
  );
}

function DuesList({ data, can, onVoid, names, onCard }) {
  if (!data.items.length) return <Empty>No monthly dues recorded for this selection.</Empty>;
  return (
    <div className="tbl-wrap">
      <table className="tbl" data-testid="dues-table">
        <thead><tr><Th>Date paid</Th><Th>Ref.</Th><Th>Name</Th><Th>Paid as</Th><Th>Month</Th><Th>Unit</Th><Th r>Amount</Th><Th>Recorded by</Th><Th /></tr></thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={r.id} className={r.status === "void" ? "opacity-50 line-through" : ""} data-testid={`dues-row-${r.id}`}>
              <Td>{r.date}</Td><Td>{r.ref_no}</Td><Td><button data-testid={`dues-card-open-${r.id}`} onClick={() => onCard(r)} className="font-semibold text-[#5C4033] underline underline-offset-2 text-left" title="Open the yearly dues card">{r.payer_name}</button>{r.possible_duplicate_of && r.status !== "void" ? <span className="ml-1 text-xs text-[#B45309]">(same month as {r.possible_duplicate_of})</span> : null}</Td>
              <Td>{TYPE[r.payer_type]}</Td><Td>{r.month_covered}</Td><Td>{names.uName(r.unit_id)}</Td><Td r><Money c={r.amount_cents} /></Td><Td>{r.created_by_name}</Td>
              <Td><span className="inline-flex gap-3 items-center"><button data-testid={`dues-receipt-${r.id}`} onClick={() => receipt(r)} className="text-xs text-[#5C4033] font-semibold inline-flex items-center gap-1"><Printer className="h-4 w-4" /> Receipt</button>{r.status === "void" ? <span className="text-xs" title={r.void_reason}>Cancelled</span> : can("collection:write") && <button data-testid={`dues-void-${r.id}`} onClick={() => onVoid(r)} className="text-xs text-[#B91C1C] font-semibold">Cancel</button>}</span></Td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function FilterScope({ fl, setFl, meta, user }) {
  const units = (meta.units || []).filter((u) => u.gkk_id === fl.gkk);
  const unitLocked = user.role === "unit_treasurer";
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="GKK"><Select data-testid="dues-filter-scope-gkk-select" value={fl.gkk} disabled={!!user.gkk_id} onChange={(e) => setFl({ ...fl, gkk: e.target.value, unit: "" })}>
        <option value="">All GKKs (Parish-wide)</option>{meta.gkks.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select></Field>
      <Field label="Unit"><Select data-testid="dues-filter-scope-unit-select" value={fl.unit} disabled={!fl.gkk || unitLocked} onChange={(e) => setFl({ ...fl, unit: e.target.value })}>
        <option value="">All Units and GKK-level</option><option value="gkk">GKK-level only</option>{units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></Field>
    </div>
  );
}

export default function Dues() {
  const { user, meta, can } = useAuth();
  const names = useNames(meta);
  const [fl, setFl] = useState({ gkk: user.gkk_id || "", unit: user.unit_id || "", month: meta.today.slice(0, 7), q: "" });
  const [data, setData] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [card, setCard] = useState(null);
  const load = useCallback(() => {
    api.get("/dues", { params: { gkk_id: fl.gkk || undefined, unit_id: fl.unit || undefined, month: fl.month || undefined, q: fl.q || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [fl]);
  useEffect(() => { if (user.role !== "member") load(); }, [load, user.role]);
  if (user.role === "member") return <Navigate to="/" replace />;
  return (
    <div className="space-y-6">
      <PageHeader title="Monthly Dues" subtitle="Dues paid by each household or member. Kept in the GKK's own funds." />
      {can("collection:write") && <DuesForm meta={meta} user={user} onSaved={load} />}
      <Card className="space-y-4">
        <FilterScope fl={fl} setFl={setFl} meta={meta} user={user} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Month covered"><Input data-testid="dues-filter-month" type="month" value={fl.month} onChange={(e) => setFl({ ...fl, month: e.target.value })} /></Field>
          <Field label="Search name"><Input data-testid="dues-filter-search" value={fl.q} onChange={(e) => setFl({ ...fl, q: e.target.value })} /></Field>
        </div>
        {data && <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Total dues" c={data.total_cents} testid="dues-stat-total" />
            <Stat label="Payments" value={data.count} testid="dues-stat-count" />
            <Stat label="Households" value={data.households} testid="dues-stat-households" />
            <Stat label="Individuals" value={data.individuals} testid="dues-stat-individuals" />
          </div>
          <DuesList data={data} can={can} onVoid={setVoiding} names={names} onCard={setCard} />
        </>}
      </Card>
      <Unpaid fl={fl} />
      {card && <CardDialog who={card} onClose={() => setCard(null)} />}
      {voiding && <VoidDialog row={voiding} onClose={(ok) => { setVoiding(null); if (ok) load(); }} />}
    </div>
  );
}
